import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
// Link styling component (font weight, colors, decoration)
import StylesBgColorSettings from '@/components/inspector-fields/ColorField/StylesBgColorSettings';
import { TextButton } from '@/components/buttons/TextButton/TextButton';
import IconTextUnderline from '@/assets/icons/icon-text-underline.svg?react';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import type { LinkStyle } from '@builder4/shared/types/styles';
import { isColorReference, processColorUpdate } from '@builder4/shared/utils/styleHelpers';
import { useColor } from '@/hooks/useColor';
import StylesFontWeightSettings from '@/components/dropdowns/SelectField/StylesFontWeightSettings';
import { useAppSelector } from '@/store/hooks';
import { selectEditingStyleColors } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';

type StylesInspectorLinkProps = {
  className?: string;
  onClick?: () => void;
  value?: LinkStyle; // External link style state
  onChange?: (next: LinkStyle) => void; // Notify parent about updates
  currentFontFamily: string; // Font family inherited from parent text
};

export const StylesInspectorLink: React.FC<StylesInspectorLinkProps> = ({
  className = '',
  onClick,
  value,
  onChange,
  currentFontFamily,
}) => {
  const [currentWeight, setCurrentWeight] = useState<string>(() => value?.fontWeight || '400');
  useEffect(() => {
    if (value?.fontWeight) setCurrentWeight(String(value.fontWeight));
  }, [value?.fontWeight]);

  // If no external value provided, manage a minimal local state for decoration only
  const [localDecoration, setLocalDecoration] = useState<string>('');

  const currentDecoration = value?.decoration ?? localDecoration ?? '';

  const hasUnderline = useMemo(
    () => currentDecoration.split(' ').filter(Boolean).includes('underline'),
    [currentDecoration],
  );

  const styleColors = useAppSelector(selectEditingStyleColors);
  const palettesForPicker = useMemo(
    () => (styleColors ? [createPaletteFromGlobalColors(styleColors)] : []),
    [styleColors],
  );

  const fontColor = useColor(value?.fontColor, styleColors ?? undefined);
  const backgroundColor = useColor(value?.backgroundColor, styleColors ?? undefined);

  const emitChange = (nextDecoration: string) => {
    if (onChange) {
      const next: LinkStyle = {
        // Preserve all existing values from value prop
        fontColor: value?.fontColor ?? { r: 0, g: 0, b: 0, a: 1 },
        backgroundColor: value?.backgroundColor ?? null,
        fontWeight: value?.fontWeight ?? '400',
        decoration: nextDecoration,
      };
      onChange(next);
    } else {
      setLocalDecoration(nextDecoration);
    }
  };

  const toggleUnderline = () => {
    // Disable underline toggle when text color is a gradient
    if (fontColor.isGradient) {
      return;
    }
    const parts = currentDecoration.split(' ').filter(Boolean);
    const withoutNone = parts.filter((p) => p !== 'none');
    const nextParts = withoutNone.includes('underline')
      ? withoutNone.filter((p) => p !== 'underline')
      : [...withoutNone, 'underline'];
    emitChange(nextParts.length > 0 ? nextParts.join(' ') : 'none');
  };

  return (
    <div
      onClick={onClick}
      style={{ width: 248 }}
      className={clsx('flex flex-col gap-3', className)}
    >
      <div className="flex flex-col gap-3 p-3">
        {/* Title handled by parent dropdown header */}

        <StylesFontWeightSettings
          className="w-full"
          fontFamily={currentFontFamily}
          value={currentWeight}
          onChange={(weight: string) => {
            setCurrentWeight(weight);
            if (onChange && value) {
              onChange({
                ...value,
                fontWeight: weight,
              });
            }
          }}
        />

        {/* Text color and hover */}
        <ColorField
          className="w-full"
          inputValue={fontColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Text Color"
          type="text"
          color={fontColor.resolvedColor || undefined}
          gradient={fontColor.gradient}
          mode={fontColor.mode}
          canClear={false}
          palettes={palettesForPicker}
          onChange={(col) => {
            if (!onChange || !value) return;
            if (typeof col === 'string' && isColorReference(col)) {
              onChange({ ...value, fontColor: col });
              return;
            }
            if (typeof col === 'string') return;
            const processed = processColorUpdate(col);
            if (processed === undefined || processed === null) return;
            onChange({ ...value, fontColor: processed });
          }}
          onChangeComplete={(col) => {
            if (!onChange || !value) return;
            if (typeof col === 'string' && isColorReference(col)) {
              onChange({ ...value, fontColor: col });
              return;
            }
            if (typeof col === 'string') return;
            const processed = processColorUpdate(col);
            if (processed === undefined || processed === null) return;
            onChange({ ...value, fontColor: processed });
          }}
        />

        <StylesBgColorSettings
          className="w-full"
          text="Background Color"
          value={backgroundColor.resolvedColor}
          palettes={palettesForPicker}
          onChange={(col) => {
            if (!onChange) return;
            if (col === null || col === undefined) {
              onChange({ ...(value || {}), backgroundColor: null } as LinkStyle);
              return;
            }
            if (typeof col === 'string' && isColorReference(col)) {
              onChange({ ...(value || {}), backgroundColor: col } as LinkStyle);
              return;
            }
            if (typeof col === 'string') return;
            const processed = processColorUpdate(col);
            if (processed === undefined) return;
            onChange({ ...(value || {}), backgroundColor: processed } as LinkStyle);
          }}
          onChangeComplete={(col) => {
            if (!onChange) return;
            if (col === null || col === undefined) {
              onChange({ ...(value || {}), backgroundColor: null } as LinkStyle);
              return;
            }
            if (typeof col === 'string' && isColorReference(col)) {
              onChange({ ...(value || {}), backgroundColor: col } as LinkStyle);
              return;
            }
            if (typeof col === 'string') return;
            const processed = processColorUpdate(col);
            if (processed === undefined) return;
            onChange({ ...(value || {}), backgroundColor: processed } as LinkStyle);
          }}
        />

        {/* Formatting controls: Only underline toggle for links */}
        <div className="flex items-center justify-between gap-1">
          <div
            className={clsx({
              'cursor-not-allowed opacity-50': fontColor.isGradient,
            })}
            title={
              fontColor.isGradient ? 'Underline is not available with gradient text' : undefined
            }
          >
            <TextButton
              onClick={toggleUnderline}
              icon={IconTextUnderline}
              state={hasUnderline ? 'active' : 'default'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StylesInspectorLink;
